/**
 * Picks which curriculum template a section's report card renders.
 *
 * Extracted from FacsimileDetailsPV. The card renders the *template*, not the
 * subjects actually assigned to the section: an unmatched template slot still
 * produces a row with empty grade cells, and a subject assigned to the section
 * whose category is not in the template never appears at all.
 *
 * The templates themselves live in src/constants.js, joined to Subject records
 * by plain string equality on subjectCategory.
 */
import constants from '../../constants';

/**
 * @param {object} section  a Section record, needs levelType and gradeLevel
 * @returns {Array|object}  a flat array for most levels; for the 2016 SHS
 *   curriculum an object keyed Core1 / AppliedAndSpecializedSubjects1 / Core2 /
 *   AppliedAndSpecializedSubjects2; for Strengthened SHS an object keyed
 *   Core / Electives.
 */
/**
 * Flattens a subject template into a plain list of slots.
 *
 * Most templates are already an array. The Strengthened SHS Grade 11 template
 * is an object of named blocks ({ Core, Electives }), because the card is meant
 * to group them - and a caller that assumed an array silently rendered nothing
 * at all for the one curriculum DM 012 introduced. Anything that just wants the
 * subjects should come through here.
 */
/**
 * The two MAPEH sub-rows the registrar's SY 2026-2027 form shows.
 *
 * Grades 4 and 5 already carry single Subject records for each pair. Grade 6
 * and JHS teach the four components separately, so their rows are averaged into
 * these two rather than printed individually - the form has two lines under
 * MAPEH, not four.
 */
/**
 * Two entries in the subject vocabulary mean the same learning area.
 *
 * "Physical Education and Health" stores PEH; "PE and Health" stores the
 * literal "PE and Health". Every curriculum template matches PEH, so a section
 * whose PE subject was created under the other one printed a blank row - and
 * because the two read identically in the dropdown, the whole school picked the
 * one the card could not see: in SY 2026-2027 all seven sections that teach PE,
 * Grades 4 through 10, used "PE and Health", so PE and Health was missing from
 * every card at every level.
 *
 * Matching through here rather than by string equality means the card no longer
 * depends on which of the two was chosen. The duplicate is gone from the
 * vocabulary so new subjects cannot repeat it, and the alias keeps the subjects
 * already created working without touching their records.
 */
const CATEGORY_ALIASES = [
  ['PEH', 'PE and Health'],
  // DepEd renamed ESP to GMRC / Values Education. Live subjects are all
  // categorised Values, while the Grade 6 and Grades 9-10 templates still
  // asked for ESP - so those rows printed blank at three grade levels. The
  // alias matches either, which also keeps a historical card finding its ESP
  // subjects rather than losing them to the rename.
  ['Values', 'Edukasyon sa Pagpapakatao (ESP)'],
];

export function categoryMatches(slotValue, subjectCategory) {
  if (slotValue === subjectCategory) return true;

  return CATEGORY_ALIASES.some(function (group) {
    return group.indexOf(slotValue) !== -1 && group.indexOf(subjectCategory) !== -1;
  });
}

/** True when any of these slot values matches the subject's category. */
export function categoryInList(values, subjectCategory) {
  return (values || []).some(function (v) {
    return categoryMatches(v, subjectCategory);
  });
}

export const MAPEH_PAIRS = [
  { value: 'Music and Arts', text: 'Music and Arts', members: ['Music', 'Arts'] },
  { value: 'PEH', text: 'PE and Health', members: ['Physical Education', 'Health'] },
];

/**
 * Collapses separately-taught MAPEH components into the form's two sub-rows.
 *
 * A template that already pairs them is returned untouched. Otherwise the first
 * component slot is replaced by the pairs whose members the template actually
 * lists, and the remaining component slots are dropped.
 */
export function pairMapehRows(slots) {
  var list = slots || [];
  var present = {};
  list.forEach(function (slot) { present[slot.value] = true; });

  var separate = MAPEH_PAIRS.reduce(function (acc, pair) {
    return acc.concat(pair.members.filter(function (m) { return present[m]; }));
  }, []);
  if (separate.length === 0) return list;

  var emitted = false;
  return list.reduce(function (out, slot) {
    if (separate.indexOf(slot.value) === -1) return out.concat(slot);
    if (emitted) return out;
    emitted = true;
    return out.concat(MAPEH_PAIRS.filter(function (pair) {
      return pair.members.some(function (m) { return present[m]; });
    }));
  }, []);
}

export function flattenTemplate(template) {
  if (!template) return [];
  if (Array.isArray(template)) return template;
  if (typeof template !== 'object') return [];

  return Object.keys(template).reduce(function (slots, blockName) {
    var block = template[blockName];
    return Array.isArray(block) ? slots.concat(block) : slots;
  }, []);
}

export function resolveSubjectTemplate(section, scheme) {
  if (!section) return constants.subjectCategory;

  const levelType = section.levelType;
  const gradeLevel = section.gradeLevel;

  // Grade 3's revised list applies from SY 2026-2027 only. Before that it still
  // falls through to subjectElemP, so historical cards reprint unchanged.
  const isReformYear = !!scheme && scheme.mode !== 'numeric-4q';

  if (levelType === 'ELEMP') {
    if (gradeLevel === 'Grade 1') return constants.subjectGrade1;
    if (gradeLevel === 'Grade 2') return constants.subjectGrade2;
    if (gradeLevel === 'Grade 3' && isReformYear) return constants.subjectGrade3;
    return constants.subjectElemP;
  }

  if (levelType === 'ELEMI') {
    if (gradeLevel === 'Grade 4') return constants.subjectGrade4;
    if (gradeLevel === 'Grade 5') return constants.subjectGrade5;
    return constants.subjectElemI;
  }

  if (levelType === 'JHS') {
    if (gradeLevel === 'Grade 7' || gradeLevel === 'Grade 8') {
      return constants.subjectGrade7and8;
    }
    return constants.subjectJHS;
  }

  // Strengthened SHS (DM 012, s. 2026): no strands, five cores across the whole
  // year. Checked before the strand branches because these level types are new.
  if (levelType === 'SHSACAD' || levelType === 'SHSTECHPRO') {
    return constants.subjectSHS11;
  }

  // The 2016 SHS curriculum, still in use for Grade 12 through SY 2026-2027.
  // Note the HUMMS misspelling: live Section records contain that literal
  // string, so it must be matched exactly rather than corrected.
  if (levelType === 'SHSHUMSS') {
    return gradeLevel === 'Grade 11 - HUMMS'
      ? constants.subjectHUMSSSHS11
      : constants.subjectHUMSSSHS12;
  }
  if (levelType === 'SHSSTEM') {
    return gradeLevel === 'Grade 11 - STEM'
      ? constants.subjectSTEMSHS11
      : constants.subjectSTEMSHS12;
  }
  if (levelType === 'SHSABM') {
    return gradeLevel === 'Grade 11 - ABM'
      ? constants.subjectABMSHS11
      : constants.subjectABMSHS12;
  }

  // levelType empty, "ELEM", "HS" or "NA" lands here and renders the entire
  // 76-entry vocabulary as mostly-blank rows. Preserved because existing
  // sections rely on it, but isFallbackTemplate() lets callers warn.
  return constants.subjectCategory;
}

/**
 * True when the section's levelType did not match any curriculum, so the card
 * is about to render the whole subject vocabulary. Almost always a data problem
 * with the section rather than an intended layout.
 */
export function isFallbackTemplate(section, scheme) {
  return resolveSubjectTemplate(section, scheme) === constants.subjectCategory;
}

/** The heading printed above the grade table. */
export function departmentLabel(levelType) {
  if (levelType === 'PRESCHOOL') return 'PRESCHOOL LEVEL';
  if (levelType === 'ELEMP' || levelType === 'ELEMI') return 'ELEMENTARY LEVEL';
  if (levelType === 'JHS') return 'JUNIOR HIGH SCHOOL LEVEL';
  if (
    levelType === 'SHSSTEM' || levelType === 'SHSHUMSS' || levelType === 'SHSABM' ||
    levelType === 'SHSACAD' || levelType === 'SHSTECHPRO'
  ) {
    return 'SENIOR HIGH SCHOOL LEVEL';
  }
  return '';
}

/**
 * Rows the card hides for a specific grade level even though they sit in that
 * level's template.
 *
 * Grade 3 still needs its combined Science/English suppression while it is on
 * subjectElemP; from SY 2026-2027 it has its own list, which simply does not
 * contain that row. The Grade 1 rule is kept as the card has always had it.
 */
export function isSuppressedRow(gradeLevel, subjectCategoryValue) {
  if (gradeLevel === 'Grade 1') {
    return subjectCategoryValue === 'English' || subjectCategoryValue === 'Science';
  }
  if (gradeLevel === 'Grade 3') {
    return subjectCategoryValue === 'SciEnglish';
  }
  return false;
}

/**
 * MAPEH has no Subject record; the card synthesises it from whichever of these
 * are assigned. "Music and Arts" and "PEH" are the Grade 4/5/7/8 pairings.
 */
export const MAPEH_COMPONENT_CATEGORIES = [
  'Music',
  'Arts',
  'Physical Education',
  'Health',
  'Music and Arts',
  'PEH',
  'PE and Health',
];

export function isMapehComponent(subjectCategoryValue) {
  return MAPEH_COMPONENT_CATEGORIES.indexOf(subjectCategoryValue) !== -1;
}
